import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCalendarCheck } from '@fortawesome/pro-regular-svg-icons';
import {
    faBoxArchive,
    faCheck,
    faExclamationTriangle,
    faHand,
    faHeart,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { BadgeOrigin, BadgeProps } from '@mui/material';

export interface PersonIndicators {
    isAlert?: boolean;
    isArchived?: boolean;
    isCheckedIn?: boolean;
    isOnCall?: boolean;
    isOnHold?: boolean;
    isPinned?: boolean;
    isRemovable?: boolean;
    isResolved?: boolean;
}

export type PersonStatus =
    | 'alert'
    | 'removable'
    | 'resolved'
    | 'onHold'
    | 'archived'
    | 'checkedIn'
    | 'pinned'
    | 'onCall';

/** The first status whose indicator is set wins. */
export const PersonStatusPriority: PersonStatus[] = [
    'alert',
    'removable',
    'resolved',
    'onHold',
    'archived',
    'checkedIn',
    'pinned',
    'onCall',
];

export const PersonStatusIndicators: Record<PersonStatus, keyof PersonIndicators> = {
    alert: 'isAlert',
    removable: 'isRemovable',
    resolved: 'isResolved',
    onHold: 'isOnHold',
    archived: 'isArchived',
    checkedIn: 'isCheckedIn',
    pinned: 'isPinned',
    onCall: 'isOnCall',
};

export type PersonStatusBadgeConfig = {
    icon?: IconDefinition;
    color?: BadgeProps['color'];
    anchor?: BadgeOrigin;
    iconScale?: number;
};

const BottomRight: BadgeOrigin = { vertical: 'bottom', horizontal: 'right' };
const TopRight: BadgeOrigin = { vertical: 'top', horizontal: 'right' };

export const PersonStatusBadges: Record<PersonStatus, PersonStatusBadgeConfig> = {
    alert: {
        icon: faExclamationTriangle,
        color: 'error',
        anchor: BottomRight,
        iconScale: 0.75,
    },
    removable: { icon: faXmark, color: 'black', anchor: TopRight },
    resolved: { icon: faCheck, color: 'success', anchor: BottomRight },
    onHold: { icon: faHand, color: 'warning', anchor: BottomRight },
    archived: { icon: faBoxArchive, color: 'black', anchor: BottomRight },
    checkedIn: { icon: faCalendarCheck, color: 'success', anchor: BottomRight },
    pinned: { icon: faHeart, color: 'primary', anchor: TopRight },
    onCall: { color: 'success', anchor: BottomRight },
};

export const resolvePersonStatus = (indicators: PersonIndicators): PersonStatus | undefined =>
    PersonStatusPriority.find((status) => indicators[PersonStatusIndicators[status]]);

export interface PersonName {
    firstName?: string;
    preferredName?: string;
    lastName?: string;
}

/** A preferred name displaces the legal first name. */
export const getPersonInitials = ({ firstName, preferredName, lastName }: PersonName): string =>
    `${preferredName ? preferredName[0] : firstName?.[0] ?? ''}${
        lastName?.[0] ?? ''
    }`.toLocaleUpperCase();

export type PersonProgressColor = 'info' | 'warning' | 'error';

/**
 * Only the pinned badge takes on the progress ring's color, and only when the ring is
 * warning or error. Every other status keeps its own color.
 */
export const getBadgeTint = (status?: PersonStatus, progressColor?: string): BadgeProps['color'] =>
    status === 'pinned' && (progressColor === 'warning' || progressColor === 'error')
        ? (progressColor as PersonProgressColor)
        : undefined;
